import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../contexts/AuthContext';
import { fetchSchedule, saveScheduleOrder, searchSchedulePlayers, addSchedulePlayer } from './curveApi';
import { resolveLabel, fmtV } from './curveFormat';

const SLEEPERCDN = 'https://sleepercdn.com/content/nfl/players/thumb/';

/* Admin: the upcoming schedule. Air dates and their landmarks are fixed
   slots on the left; player cards on the right drag (or arrow) between
   them. Save writes the new order. */
const ROW_H = 56;
const styles = `
  .cvs-page { min-height: calc(100vh - 60px); background: #FFFFFF; color: #1D1D1F;
    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
    --brand:#FF7A00; --brand-soft:rgba(255,122,0,.13); --brand-ink:#C25E00;
    --ink:#1D1D1F; --ink2:#6E6E73; --ink3:#86868B; --surface:#F5F5F7; --line:#EBEBED;
    --green-ink:#1B873F; --green-soft:rgba(52,199,89,.14);
    --mono:'SF Mono', ui-monospace, 'Menlo', monospace; --ease:cubic-bezier(0.16,1,0.3,1); }
  .cvs-page * { box-sizing: border-box; }
  .cvs-wrap { max-width: 680px; margin: 0 auto; padding: 34px 18px 140px; }
  .cvs-kick { font-family: var(--mono); font-size: 10.5px; font-weight: 700; letter-spacing: .2em;
    color: var(--brand-ink); margin-bottom: 12px; }
  .cvs-h { font-size: 28px; font-weight: 700; letter-spacing: -.03em; margin-bottom: 6px; }
  .cvs-sub { font-size: 14.5px; color: var(--ink2); line-height: 1.5; margin-bottom: 26px; }

  .cvs-grid { display: grid; grid-template-columns: 128px 1fr; gap: 8px; }
  .cvs-slot { height: ${ROW_H}px; display: flex; flex-direction: column; justify-content: center; }
  .cvs-slot .d { font-family: var(--mono); font-size: 11px; font-weight: 700; letter-spacing: .04em; }
  .cvs-slot .lm { font-family: var(--mono); font-size: 8px; font-weight: 700; letter-spacing: .08em;
    color: var(--ink3); margin-top: 3px; }
  .cvs-slot .lm.flip { color: var(--brand-ink); }

  .cvs-card { height: ${ROW_H}px; display: flex; align-items: center; gap: 10px; background: var(--surface);
    border-radius: 12px; padding: 0 6px 0 12px; }
  .cvs-card.dragging { background: #fff; box-shadow: 0 12px 30px rgba(0,0,0,.16); }
  .cvs-grip { color: var(--ink3); flex: none; cursor: grab; touch-action: none; padding: 8px 2px;
    display: flex; align-items: center; }
  .cvs-grip svg { width: 13px; height: 13px; }
  .cvs-ava { width: 34px; height: 34px; border-radius: 50%; background: #D2D2D7 center/cover no-repeat; flex: none; }
  .cvs-who { min-width: 0; }
  .cvs-nm { font-size: 14px; font-weight: 700; letter-spacing: -.01em; white-space: nowrap;
    overflow: hidden; text-overflow: ellipsis; }
  .cvs-mt { font-family: var(--mono); font-size: 8.5px; color: var(--ink3); margin-top: 2px; letter-spacing: .05em; }
  .cvs-arrows { margin-left: auto; display: flex; flex: none; }
  .cvs-page .cvs-arw { width: 40px; height: 44px; border: none; background: none; cursor: pointer;
    color: var(--ink2); font-size: 15px; border-radius: 10px; }
  .cvs-page .cvs-arw:hover:not(:disabled) { background: #EBEBED; color: var(--ink); }
  .cvs-page .cvs-arw:disabled { opacity: .25; cursor: default; }

  .cvs-add { margin: 0 0 26px; }
  .cvs-addin { width: 100%; background: var(--surface); border: none; border-radius: 12px;
    font: inherit; font-size: 15px; padding: 13px 14px; color: var(--ink); outline: none; }
  .cvs-addin::placeholder { color: var(--ink3); }
  .cvs-addin:focus { background: #fff; box-shadow: 0 0 0 3px rgba(0,0,0,.07); }
  .cvs-res { display: flex; align-items: center; gap: 10px; padding: 9px 4px; border-bottom: 1px solid var(--line); }
  .cvs-res:last-child { border-bottom: none; }
  .cvs-flag { margin-left: auto; flex: none; font-family: var(--mono); font-size: 9px; font-weight: 700;
    letter-spacing: .08em; color: var(--ink3); }
  .cvs-page .cvs-addbtn { margin-left: auto; flex: none; background: #000; color: #fff; border: none; cursor: pointer;
    border-radius: 980px; padding: 8px 18px; font: inherit; font-size: 13px; font-weight: 600; }
  .cvs-page .cvs-addbtn:disabled { opacity: .4; cursor: default; }
  .cvs-adderr { font-size: 12.5px; color: #FF3B30; margin-top: 8px; }
  .cvs-addnote { font-family: var(--mono); font-size: 10px; letter-spacing: .06em; color: var(--ink3); margin-top: 8px; }

  .cvs-savebar { position: fixed; left: 0; right: 0; bottom: 0; display: flex; justify-content: center;
    padding: 14px 18px calc(14px + env(safe-area-inset-bottom)); pointer-events: none;
    background: linear-gradient(transparent, rgba(255,255,255,.92) 40%); }
  .cvs-page .cvs-save { pointer-events: auto; background: #000; color: #fff; border: none; cursor: pointer;
    border-radius: 980px; padding: 15px 44px; font: inherit; font-size: 15px; font-weight: 600;
    box-shadow: 0 10px 30px rgba(0,0,0,.25); }
  .cvs-page .cvs-save:disabled { opacity: .6; cursor: default; }
  .cvs-note { text-align: center; font-family: var(--mono); font-size: 10px; letter-spacing: .08em;
    color: var(--ink3); margin-top: 18px; }
  .cvs-state { text-align: center; padding: 80px 20px; color: var(--ink2); font-size: 15px; }

  @media (max-width: 600px) {
    .cvs-grid { grid-template-columns: 92px 1fr; }
    .cvs-slot .d { font-size: 10px; }
  }
`;

const Grip = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <circle cx="7" cy="4" r="1.6" /><circle cx="13" cy="4" r="1.6" />
    <circle cx="7" cy="10" r="1.6" /><circle cx="13" cy="10" r="1.6" />
    <circle cx="7" cy="16" r="1.6" /><circle cx="13" cy="16" r="1.6" />
  </svg>
);

function fmtSlotDate(dateStr) {
  return new Date(`${dateStr}T12:00:00Z`)
    .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    .toUpperCase();
}

function PlayerCard({ entry, index, count, onMove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: entry.id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 5 : undefined, position: 'relative' };
  return (
    <div ref={setNodeRef} style={style} className={`cvs-card${isDragging ? ' dragging' : ''}`}>
      <span className="cvs-grip" {...attributes} {...listeners}><Grip /></span>
      <span className="cvs-ava" style={entry.player.sleeperId ? { backgroundImage: `url(${SLEEPERCDN}${entry.player.sleeperId}.jpg)` } : undefined} />
      <span className="cvs-who">
        <span className="cvs-nm" style={{ display: 'block' }}>{entry.player.name}</span>
        <span className="cvs-mt" style={{ display: 'block' }}>
          {[entry.player.position, entry.player.value ? fmtV(entry.player.value) : null].filter(Boolean).join(' · ')}
        </span>
      </span>
      <span className="cvs-arrows">
        <button type="button" className="cvs-arw" disabled={index === 0} onClick={() => onMove(index, index - 1)} aria-label="Move up">↑</button>
        <button type="button" className="cvs-arw" disabled={index === count - 1} onClick={() => onMove(index, index + 1)} aria-label="Move down">↓</button>
      </span>
    </div>
  );
}

export default function CurveSchedule() {
  const { user, loading: authLoading } = useAuth();
  const [slots, setSlots] = useState(null); // fixed: [{lineDate, resolveDate}]
  const [entries, setEntries] = useState(null); // ordered player payloads keyed by line id
  const [originalIds, setOriginalIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [canAdd, setCanAdd] = useState(false); // schedule owner only (server decides)
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [addingId, setAddingId] = useState(null);
  const [addError, setAddError] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const applySchedule = (schedule) => {
    setSlots(schedule.map((e) => ({ lineDate: e.lineDate, resolveDate: e.resolveDate })));
    setEntries(schedule);
    setOriginalIds(schedule.map((e) => e.id));
  };

  const load = () => fetchSchedule().then(({ schedule, canAdd: allowed }) => {
    applySchedule(schedule);
    setCanAdd(!!allowed);
  });

  useEffect(() => {
    if (user && user.isAdmin) load().catch(() => setEntries([]));
  }, [user]);

  // Debounced player search for the add box.
  useEffect(() => {
    if (!canAdd || query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const t = setTimeout(() => {
      searchSchedulePlayers(query)
        .then(({ players }) => setResults(players || []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query, canAdd]);

  const dirty = useMemo(
    () => !!entries && (entries.length !== originalIds.length || entries.some((e, i) => e.id !== originalIds[i])),
    [entries, originalIds]
  );

  if (authLoading) return null;
  if (!user || !user.isAdmin) return <Navigate to="/curve" replace />;

  const move = (from, to) => setEntries((cur) => arrayMove(cur, from, to));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setEntries((cur) => arrayMove(cur, cur.findIndex((e) => e.id === active.id), cur.findIndex((e) => e.id === over.id)));
  };

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const { schedule } = await saveScheduleOrder(entries.map((e) => e.id));
      applySchedule(schedule);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
    } catch (e) {
      // stale schedule (a line opened since load) — refresh and let them redo
      await load().catch(() => {});
    } finally {
      setSaving(false);
    }
  };

  const addPlayer = async (p) => {
    if (addingId || dirty) return;
    setAddingId(p.id);
    setAddError(null);
    try {
      const { schedule } = await addSchedulePlayer(p.id);
      applySchedule(schedule);
      setQuery('');
      setResults([]);
    } catch (e) {
      setAddError(e.message || 'Could not add that player');
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div className="cvs-page">
      <style>{styles}</style>
      <div className="cvs-wrap">
        <div className="cvs-kick">THE CURVE · SCHEDULE</div>
        <div className="cvs-h">Who drops when.</div>
        <div className="cvs-sub">
          Drag a player to a new air date — dates and their landmarks stay put, players move.
          Today's line is live and can't move. The schedule refills itself when it runs short.
        </div>

        {canAdd ? (
          <div className="cvs-add">
            <input
              className="cvs-addin"
              placeholder="Add a player — search by name"
              aria-label="Search players to add"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setAddError(null); }}
            />
            {results.map((p) => (
              <div className="cvs-res" key={p.id}>
                <span className="cvs-ava" style={p.sleeperId ? { backgroundImage: `url(${SLEEPERCDN}${p.sleeperId}.jpg)` } : undefined} />
                <span className="cvs-who">
                  <span className="cvs-nm" style={{ display: 'block' }}>{p.name}</span>
                  <span className="cvs-mt" style={{ display: 'block' }}>
                    {[p.position, p.team, p.value ? fmtV(p.value) : null].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {p.scheduled ? (
                  <span className="cvs-flag">IN THE SCHEDULE</span>
                ) : (
                  <button
                    type="button"
                    className="cvs-addbtn"
                    disabled={!!addingId || dirty}
                    onClick={() => addPlayer(p)}
                  >
                    {addingId === p.id ? 'Adding…' : 'Add'}
                  </button>
                )}
              </div>
            ))}
            {addError ? <div className="cvs-adderr">{addError}</div> : null}
            {dirty && query.trim().length >= 2 ? (
              <div className="cvs-addnote">SAVE THE NEW ORDER FIRST — ADDING REFRESHES THE LIST</div>
            ) : null}
          </div>
        ) : null}

        {!entries ? (
          <div className="cvs-state">Loading the slate…</div>
        ) : !entries.length ? (
          <div className="cvs-state">Nothing scheduled. The nightly refill will top this up.</div>
        ) : (
          <div className="cvs-grid">
            <div>
              {slots.map((s, i) => {
                const label = resolveLabel(s.resolveDate);
                const flipped = i > 0 && slots[i - 1].resolveDate !== s.resolveDate;
                return (
                  <div className="cvs-slot" style={{ marginBottom: 8 }} key={s.lineDate}>
                    <span className="d">{fmtSlotDate(s.lineDate)}</span>
                    <span className={`lm${flipped ? ' flip' : ''}`}>→ {label.toUpperCase()}</span>
                  </div>
                );
              })}
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={entries.map((e) => e.id)} strategy={verticalListSortingStrategy}>
                <div>
                  {entries.map((e, i) => (
                    <div style={{ marginBottom: 8 }} key={e.id}>
                      <PlayerCard entry={e} index={i} count={entries.length} onMove={move} />
                    </div>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {entries && entries.length ? (
          <div className="cvs-note">{entries.length} DAYS SCHEDULED · THROUGH {fmtSlotDate(slots[slots.length - 1].lineDate)}</div>
        ) : null}
      </div>

      {dirty || savedFlash ? (
        <div className="cvs-savebar">
          <button type="button" className="cvs-save" disabled={saving || !dirty} onClick={save}>
            {saving ? 'Saving…' : savedFlash && !dirty ? 'Saved' : 'Save the new order'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
